import { Component, Input, OnDestroy } from '@angular/core';
import { Principal } from '../../../../shared';
import { TranslateService } from '@ngx-translate/core';
import { ExperimentalDesignParameters } from '../../../tabs/experimental-design/experimental-design.parameters.model';
import { Subject } from 'rxjs';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StudyInstanceItem } from '../../study-instances-table-selector/study-instances-table-selector.component';
import { ExperimentalDesignTypeEnum } from '../../../../shared/experimental.design/experimental-design-type.enum';
import { EDIT_TREATMENT_FACTORS_PERMISSIONS } from '../../../../shared/auth/permissions';
import { StudyDetails } from '../../../../shared/study/model/study-details.model';
import { ObservationVariable } from '../../../../shared/model/observation-variable.model';
import { ExperimentalDesignContext } from '../../../tabs/experimental-design/experimental-design-context';
import { ActivatedRoute, Router } from '@angular/router';
import { TreatmentFactorLabelInstance, TreatmentFactorsData } from '../../../tabs/experimental-design/treatment-factors.model';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';

@Component({
    selector: 'jhi-edit-treatment-factors-modal',
    templateUrl: './edit-treatment-factors-modal.component.html'
})
export class EditTreatmentFactorsModalComponent implements OnDestroy {

    @Input() studyInstanceItems: StudyInstanceItem[] = [];
    @Input() experimentDesignInput: ExperimentalDesignParameters;

    datasetId: number;
    isSaving: false;
    hideSummary = false;
    summaryMap = new Map<number, Map<number, any>>();

    private confirmSubject = new Subject<StudyInstanceItem[]>();
    onContinue$ = this.confirmSubject.asObservable();

    studyDetails: StudyDetails;
    entryDetailSettingsMap: Map<number, ObservationVariable>;
    totalGermplasmEntryListCount: number;
    computedPlotsCount: number;
    treatmentFactorsData: TreatmentFactorsData;
    excludedCombinations: TreatmentFactorLabelInstance[] = [];
    sortedInstancesMap = new Map<string, any[]>();
    plotCountPerInstance = new Map<number, number>();
    disabledDeleteMessage: string;

    constructor(
        public principal: Principal,
        public modalService: NgbModal,
        public activeModal: NgbActiveModal,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        public translateService: TranslateService,
        public experimentalDesignContext: ExperimentalDesignContext,
    ) {
    }

    ngOnInit(): void {
        this.studyDetails = this.experimentalDesignContext.studyDetails;
        this.entryDetailSettingsMap = this.experimentalDesignContext.entryDetailSettingsMap;
        this.totalGermplasmEntryListCount = this.experimentalDesignContext.totalGermplasmEntryListCount;

        this.computedPlotsCount = this.experimentDesignInput.replicationsCount * this.totalGermplasmEntryListCount;
        const treatmentFactors = this.studyDetails.treatmentFactors;
        for (const [treatmentKey, treatmentVar] of treatmentFactors.entries()) {
            this.computedPlotsCount *= treatmentFactors[treatmentKey].values.length;
        }

        this.treatmentFactorsData = this.experimentDesignInput.treatmentFactorsData;
        this.flattenAndSortData();

        this.disabledDeleteMessage = this.translateService.instant('study-editor.experiment-design.edit-treatment-factors.delete');
    }

    ngOnDestroy(): void {
        this.confirmSubject.complete();
    }

    flattenAndSortData() {
        Object.keys(this.treatmentFactorsData).forEach(key => {
            let sortedInstances = [];
            const treatmentFactor = this.treatmentFactorsData[key];

            this.studyInstanceItems.forEach(instance => {
                let i = 1;
                this.plotCountPerInstance.set(instance.instanceNumber, this.computedPlotsCount);
                if (treatmentFactor.categoricalValues && treatmentFactor.categoricalValues.length > 0) {
                    const categoricalValuesMap = new Map(
                        treatmentFactor.categoricalValues?.map(item => [item.id, item.name])
                    );
                    treatmentFactor.labels.forEach(label => {
                        sortedInstances.push({ ...instance, 'label': label, 'idx': i, 'labelCategorical': true, 'categoryName': categoricalValuesMap.get(label) });
                        i++;
                    });
                } else {
                    treatmentFactor.labels.forEach(label => {
                        sortedInstances.push({ ...instance, 'label': label, 'idx': i, 'labelCategorical': false });
                        i++;
                    });
                }

                // Create nested map: instanceNumber -> variableId -> object containing all attributes
                const summaryItem = {
                    ...instance,
                    'alias': treatmentFactor.alias,
                    'variableId': key,
                    'levelCount': i - 1
                };

                if (!this.summaryMap.has(instance.instanceNumber)) {
                    this.summaryMap.set(instance.instanceNumber, new Map());
                }
                this.summaryMap.get(instance.instanceNumber)
                    .set(+key, summaryItem);
            });
            sortedInstances.sort((a, b) => a.instanceNumber - b.instanceNumber);
            this.sortedInstancesMap.set(key, sortedInstances);
        });
        this.summaryMap = new Map(
            Array.from(this.summaryMap.entries())
                .sort((a, b) => a[0] - b[0])
        );
    }

    getSelectedInstanceIds(): number[] {
        return this.studyInstanceItems.filter((instance) => instance.isChecked).map((instance) => instance.instanceId);
    }

    getSelectedStudyInstanceItems(): StudyInstanceItem[] {
        return this.studyInstanceItems.filter((instance) => instance.isChecked);
    }

    cancel() {
        const modalConfirmDelete = this.modalService.open(ModalConfirmComponent);
        modalConfirmDelete.componentInstance.message = this.translateService.instant('study-editor.experiment-design.edit-treatment-factors.back');
        modalConfirmDelete.componentInstance.showButtonIcons = false;
        modalConfirmDelete.componentInstance.cancelLabel = this.translateService.instant('no');
        modalConfirmDelete.componentInstance.confirmLabel = this.translateService.instant('yes');
        modalConfirmDelete.result.then(() => {
            this.activeModal.dismiss();
        });
    }

    continue() {
        this.experimentalDesignContext.excludedtreatmentFactors = this.excludedCombinations;
        this.activeModal.close();
    }

    showEditTreatmentFactors() {
        return (this.experimentDesignInput.designType === ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK.id
            && this.principal.hasAnyAuthorityDirect(this.EDIT_TREATMENT_FACTORS_PERMISSIONS)
            && Object.keys(this.experimentDesignInput.treatmentFactorsData).length > 0);
    }

    disableDeleteButton(instanceNo, variableId) {
        const item = this.summaryMap.get(+instanceNo).get(+variableId);
        return item.levelCount === 1;
    }

    excludeCombination(row, variableId) {
        const instanceNo = row.instanceNumber;
        const levelNo = row.idx;

        const modalConfirmDelete = this.modalService.open(ModalConfirmComponent);
        modalConfirmDelete.componentInstance.message = this.translateService.instant(
            'study-editor.experiment-design.edit-treatment-factors.confirm', {
                param1: this.treatmentFactorsData[variableId].alias,
                param2: row.labelCategorical ? row.categoryName : row.label, param3: row.locationName
            });
        modalConfirmDelete.componentInstance.showButtonIcons = false;
        modalConfirmDelete.componentInstance.cancelLabel = this.translateService.instant('no');
        modalConfirmDelete.componentInstance.confirmLabel = this.translateService.instant('yes');
        modalConfirmDelete.result.then(() => {
            const combination: TreatmentFactorLabelInstance = {
                instanceNo: instanceNo,
                levelNo: levelNo,
                variableId: variableId
            };
            this.excludedCombinations.push(combination);
            const updatedList = this.sortedInstancesMap.get(variableId)
                .filter(item => !(item.instanceNumber === instanceNo && item.idx === levelNo));
            this.sortedInstancesMap.set(variableId, updatedList);

            this.updateSummaryTable(instanceNo, variableId);
        });
    }

    private updateSummaryTable(instanceNo, variableId) {
        const tfvarMap = this.summaryMap.get(instanceNo);

        if (tfvarMap) {
            const item = tfvarMap.get(+variableId);

            if (item) {
                var currPlotCount = this.plotCountPerInstance.get(instanceNo);
                currPlotCount -= currPlotCount / item.levelCount;
                this.plotCountPerInstance.set(instanceNo, currPlotCount);
                item.levelCount--;
            } else {
                tfvarMap.set(+variableId, item);
            }
            this.summaryMap.set(instanceNo, tfvarMap);
        }
    }

    protected readonly EDIT_TREATMENT_FACTORS_PERMISSIONS = EDIT_TREATMENT_FACTORS_PERMISSIONS;
    protected readonly Object = Object;
    protected readonly Array = Array;
}

